import React, { useEffect, useMemo, useState } from "react";
import "./JubenshaMap.css";

const normalizeIds = (arr) =>
  (arr || [])
    .map((x) => (typeof x === "string" ? x : x?.id))
    .filter(Boolean);

function buildById(list) {
  return (list || []).reduce((acc, item) => {
    if (item?.id) acc[item.id] = item;
    return acc;
  }, {});
}

function IdPills({ ids, lookup, emptyText }) {
  const normalized = normalizeIds(ids);
  if (!normalized.length) return <div className="jubensha-map-empty-meta">{emptyText}</div>;
  return (
    <div className="jubensha-map-pill-row">
      {normalized.map((id) => (
        <span key={id} className="jubensha-map-pill" title={id}>
          {lookup?.[id]?.title || lookup?.[id]?.name || id}
        </span>
      ))}
    </div>
  );
}

function buildIdSet(list) {
  return new Set(
    (list || [])
      .map((x) => (typeof x === "string" ? x : x?.id))
      .filter(Boolean)
  );
}

function normalizeCost(cost) {
  const c = cost && typeof cost === "object" ? cost : {};
  return Object.fromEntries(
    Object.entries(c)
      .map(([k, v]) => [k, Number(v)])
      .filter(([, v]) => Number.isFinite(v) && v !== 0)
  );
}

function normalizeRequirement(req) {
  const r = req && typeof req === "object" ? req : {};
  return Object.fromEntries(
    Object.entries(r)
      .map(([k, v]) => [k, Number(v)])
      .filter(([, v]) => Number.isFinite(v) && v > 0)
  );
}

function normalizeRewardEntries(entries) {
  return (entries || [])
    .map((x) => {
      if (typeof x === "string") return { id: x, cost: {}, requirement: {} };
      if (!x?.id) return null;
      return {
        id: x.id,
        cost: normalizeCost(x.cost),
        requirement: normalizeRequirement(x.requirement),
      };
    })
    .filter(Boolean);
}

function StatLines({ title, obj, emptyText }) {
  const entries = Object.entries(obj || {});
  if (!entries.length) return <div className="jubensha-map-confirm-empty">{emptyText}</div>;
  return (
    <div className="jubensha-map-confirm-block">
      <div className="jubensha-map-confirm-label">{title}</div>
      <div className="jubensha-map-confirm-lines">
        {entries.map(([k, v]) => (
          <div key={k} className="jubensha-map-confirm-line">
            <span className="k">{k}</span>
            <span className="v">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function getMissingStats(seatStats, costObj) {
  const cost = costObj || {};
  const stats = seatStats || {};
  const missing = {};
  Object.entries(cost).forEach(([k, v]) => {
    const need = Number(v);
    if (!Number.isFinite(need) || need <= 0) return;
    const have = Number(stats?.[k] ?? 0);
    if (have < need) missing[k] = need - have;
  });
  return missing;
}

export default function JubenshaMap({
  title = "Game Map",
  subtitle,
  mapEntry,
  availableItems,
  availableNotes,
  availableTags,
  variant = "inspect", // 'inspect' | 'covered' | 'room-acquire'
  isRoomView = false,
  stageKey = null,
  seatPosition = null,
  seatData = null,
  onClaimReward,
  onEnterLocation,
}) {
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState(() => new Set());
  const [loadingKey, setLoadingKey] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'enter' | 'reward', ... }

  const itemsById = useMemo(() => buildById(availableItems), [availableItems]);
  const notesById = useMemo(() => buildById(availableNotes), [availableNotes]);
  const tagsById = useMemo(() => buildById(availableTags), [availableTags]);

  const mapUrl = mapEntry?.url || null;
  const locationsRaw = mapEntry?.locations || [];
  const locations = (locationsRaw || []).filter(Boolean);

  const activeLocation = activeLocationId
    ? locations.find((l) => l?.id === activeLocationId)
    : null;

  const ownedItemIds = useMemo(() => buildIdSet(seatData?.inventory || []), [seatData?.inventory]);
  const ownedNoteIds = useMemo(() => buildIdSet(seatData?.notes?.private || []), [seatData?.notes?.private]);
  const ownedTagIds = useMemo(() => buildIdSet(seatData?.tags || []), [seatData?.tags]);
  const visitedLocations = seatData?.visitedLocations || {};
  const visitedThisStage = Array.isArray(visitedLocations?.[stageKey]) ? visitedLocations[stageKey] : [];
  const hasEnteredActiveLocation = Boolean(activeLocation?.id && visitedThisStage.includes(activeLocation.id));

  useEffect(() => {
    if (!activeLocationId) return;
    const stillExists = locations.some((l) => l?.id === activeLocationId);
    if (!stillExists) setActiveLocationId(null);
  }, [locations, activeLocationId]);

  // When stage changes, player is considered back to start point.
  // Reset local UI state so they clearly need to re-enter locations for the new stage.
  useEffect(() => {
    setActiveLocationId(null);
    setConfirmModal(null);
    setLoadingKey(null);
    setRevealedKeys(new Set());
  }, [stageKey]);

  const isSeatReady = seatPosition !== null && seatPosition !== undefined;
  const claimEnabled = variant === "room-acquire" && !isRoomView;

  const openEnterModal = (loc) => {
    const cost = normalizeCost(loc?.cost);
    setConfirmModal({
      type: "enter",
      locationId: loc?.id,
      title: loc?.title || loc?.id,
      cost,
    });
  };

  const openRewardModal = (rewardType, rewardEntry) => {
    const cost = normalizeCost(rewardEntry?.cost);
    const requirement = normalizeRequirement(rewardEntry?.requirement);
    setConfirmModal({
      type: "reward",
      rewardType,
      rewardId: rewardEntry?.id,
      title: rewardEntry?.id,
      cost,
      requirement,
      locationId: activeLocation?.id,
      locationTitle: activeLocation?.title || activeLocation?.id,
    });
  };

  const confirmEnter = async () => {
    if (!confirmModal || confirmModal.type !== "enter") return;
    if (!claimEnabled) return;
    if (!isSeatReady) {
      alert("Please claim a seat first.");
      return;
    }
    if (!stageKey || !confirmModal.locationId) {
      alert("Map context missing (stage/location).");
      return;
    }
    const missing = getMissingStats(seatData?.stats, confirmModal.cost);
    if (Object.keys(missing).length) {
      alert(`Not enough stats: ${Object.entries(missing).map(([k, v]) => `${k}缺${v}`).join(", ")}`);
      return;
    }
    try {
      setLoadingKey(`enter:${confirmModal.locationId}`);
      await onEnterLocation?.({
        seatPosition,
        stageKey,
        locationId: confirmModal.locationId,
      });
      setConfirmModal(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to enter location";
      alert(msg);
    } finally {
      setLoadingKey(null);
    }
  };

  const revealCard = async (type, id) => {
    if (!id) return;
    if (!claimEnabled) return;
    if (!isSeatReady) {
      alert("Please claim a seat first, then reveal cards to receive rewards.");
      return;
    }
    if (!stageKey || !activeLocation?.id) {
      alert("Map context missing (stage/location).");
      return;
    }

    const key = `${type}:${id}`;
    if (revealedKeys.has(key)) return;
    if (loadingKey) return;

    // Pre-check cost before sending request (backend still enforces too)
    const rewardEntries = normalizeRewardEntries(activeLocation?.[type === "item" ? "items" : type === "note" ? "notes" : "tags"] || []);
    const entry = rewardEntries.find((e) => e.id === id);
    const missing = getMissingStats(seatData?.stats, entry?.cost || {});
    if (Object.keys(missing).length) {
      alert(`Not enough stats: ${Object.entries(missing).map(([k, v]) => `${k}缺${v}`).join(", ")}`);
      return;
    }

    setLoadingKey(key);
    try {
      if (typeof onClaimReward === "function") {
        const res = await onClaimReward({
          seatPosition,
          stageKey,
          locationId: activeLocation.id,
          rewardType: type,
          rewardId: id,
        });
        if (res?.ok === false && res?.reason === 'requirement_failed') {
          alert("Requirement not met. Cost consumed, nothing acquired.");
          return;
        }
      } else {
        throw new Error("Missing onClaimReward handler");
      }
    } catch (err) {
      // If the player already has it, we still flip (treat as revealed).
      const msg = err?.response?.data?.message || err?.message || "";
      const lower = String(msg).toLowerCase();
      const isAlready =
        lower.includes("already") ||
        lower.includes("assigned") ||
        lower.includes("exists") ||
        lower.includes("duplicate");
      if (!isAlready) {
        alert(msg || "Failed to reveal reward");
        return;
      }
    } finally {
      setLoadingKey(null);
    }

    setRevealedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const renderCoveredCards = (type, entries, lookup, ownedSet, emptyText) => {
    const normalizedEntries = normalizeRewardEntries(entries);
    if (!normalizedEntries.length) return <div className="jubensha-map-empty-meta">{emptyText}</div>;

    return (
      <div className="jubensha-map-card-grid">
        {normalizedEntries.map((entry) => {
          const id = entry.id;
          const key = `${type}:${id}`;
          const revealed = revealedKeys.has(key) || ownedSet.has(id);
          const isLoading = loadingKey === key;
          const titleText = lookup?.[id]?.title || lookup?.[id]?.name || id;
          const subtitleText =
            type === "item" ? "Item" : type === "note" ? "Note (Private)" : "Tag";
          const statusText = ownedSet.has(id) ? "Acquired" : revealed ? "Revealed" : "Covered";
          const disabled = !claimEnabled || isLoading || revealed;

          return (
            <button
              key={key}
              type="button"
              className={`jubensha-map-card ${revealed ? "revealed" : ""}`}
              onClick={() => {
                if (!claimEnabled) return;
                if (!hasEnteredActiveLocation) {
                  openEnterModal(activeLocation);
                  return;
                }
                openRewardModal(type, entry);
              }}
              disabled={disabled}
              title={
                revealed
                  ? titleText
                  : claimEnabled
                    ? "Click to flip"
                    : "Hidden (view-only)"
              }
            >
              <div className="jubensha-map-card-inner">
                <div className="jubensha-map-card-face jubensha-map-card-back">
                  <div className="jubensha-map-card-mark">?</div>
                  <div className="jubensha-map-card-type">{subtitleText}</div>
                  <div className="jubensha-map-card-status">
                    {!claimEnabled
                      ? "Hidden"
                      : isLoading
                        ? "Revealing..."
                        : isSeatReady
                          ? "Flip to reveal"
                          : "Claim a seat"}
                  </div>
                </div>
                <div className="jubensha-map-card-face jubensha-map-card-front">
                  <div className="jubensha-map-card-title">{titleText}</div>
                  <div className="jubensha-map-card-status">{statusText}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="jubensha-map">
      <div className="jubensha-map-top">
        <div>
          <div className="jubensha-map-label">{title}</div>
          <div className="jubensha-map-title">{subtitle || "Map"}</div>
        </div>
        <div className="jubensha-map-meta">
          <span className="jubensha-map-meta-pill">{locations.length} locations</span>
        </div>
      </div>

      {!mapUrl ? (
        <div className="jubensha-map-empty">No map configured for this stage.</div>
      ) : (
        <div className="jubensha-map-grid">
          <div className="jubensha-map-canvas" onClick={() => setActiveLocationId(null)} role="presentation">
            <img className="jubensha-map-image" src={mapUrl} alt="Game map" />
            {locations.map((loc, idx) => {
              const x = Number(loc?.x);
              const y = Number(loc?.y);
              const left = Number.isFinite(x) ? `${x}%` : "50%";
              const top = Number.isFinite(y) ? `${y}%` : "50%";
              const isActive = activeLocationId === loc?.id;
              return (
                <button
                  key={loc?.id || idx}
                  type="button"
                  className={`jubensha-map-marker ${isActive ? "active" : ""}`}
                  style={{ left, top }}
                  title={loc?.title || loc?.id || `Location ${idx + 1}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!loc?.id) return;
                    setActiveLocationId((prev) => (prev === loc.id ? null : loc.id));
                    if (claimEnabled && !isRoomView && stageKey && seatPosition !== null) {
                      // If not entered yet and there is a cost, prompt to enter
                      const cost = normalizeCost(loc?.cost);
                      const visited = seatData?.visitedLocations?.[stageKey] || [];
                      const entered = Array.isArray(visited) && visited.includes(loc.id);
                      if (!entered && Object.keys(cost).length > 0) {
                        openEnterModal(loc);
                      }
                    }
                  }}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>

          <div className="jubensha-map-side">
            {activeLocation ? (
              <>
                <div className="jubensha-map-side-title">{activeLocation.title || activeLocation.id}</div>
                <div className="jubensha-map-side-sub">
                  ({Math.round(Number(activeLocation.x))}%, {Math.round(Number(activeLocation.y))}%)
                </div>

                <div className="jubensha-map-side-block">
                  <div className="jubensha-map-side-label">Items</div>
                  {variant === "room-acquire" || variant === "covered"
                    ? renderCoveredCards("item", activeLocation.items, itemsById, ownedItemIds, "No items")
                    : <IdPills ids={activeLocation.items} lookup={itemsById} emptyText="No items" />}
                </div>

                <div className="jubensha-map-side-block">
                  <div className="jubensha-map-side-label">Notes</div>
                  {variant === "room-acquire" || variant === "covered"
                    ? renderCoveredCards("note", activeLocation.notes, notesById, ownedNoteIds, "No notes")
                    : <IdPills ids={activeLocation.notes} lookup={notesById} emptyText="No notes" />}
                </div>

                <div className="jubensha-map-side-block">
                  <div className="jubensha-map-side-label">Tags</div>
                  {variant === "room-acquire" || variant === "covered"
                    ? renderCoveredCards("tag", activeLocation.tags, tagsById, ownedTagIds, "No tags")
                    : <IdPills ids={activeLocation.tags} lookup={tagsById} emptyText="No tags" />}
                </div>
              </>
            ) : (
              <div className="jubensha-map-hint">
                Click a marker to view location details.
                {variant === "room-acquire" && <div className="jubensha-map-hint-sub">Flip covered cards to receive rewards.</div>}
                {variant === "covered" && <div className="jubensha-map-hint-sub">Rewards are hidden in the room view.</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="jubensha-map-confirm-overlay" onClick={() => setConfirmModal(null)}>
          <div className="jubensha-map-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="jubensha-map-confirm-title">
              {confirmModal.type === "enter"
                ? `Enter: ${confirmModal.title}`
                : `Try: ${confirmModal.rewardType?.toUpperCase()} @ ${confirmModal.locationTitle || ""}`}
            </div>
            <div className="jubensha-map-confirm-warning">
              If you confirm, the cost will be consumed. If requirements are not met, you get nothing.
            </div>

            {confirmModal.type === "enter" ? (
              <StatLines title="Cost (Stats)" obj={confirmModal.cost} emptyText="No cost" />
            ) : (
              <>
                <StatLines title="Cost (Stats)" obj={confirmModal.cost} emptyText="No cost" />
                <StatLines title="Requirement (Attributes min)" obj={confirmModal.requirement} emptyText="No requirement" />
              </>
            )}

            <div className="jubensha-map-confirm-actions">
              <button className="jubensha-map-btn secondary" type="button" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button
                className="jubensha-map-btn primary"
                type="button"
                onClick={async () => {
                  if (confirmModal.type === "enter") {
                    await confirmEnter();
                    return;
                  }
                  if (confirmModal.type === "reward") {
                    const { rewardType, rewardId } = confirmModal;
                    setConfirmModal(null);
                    await revealCard(rewardType, rewardId);
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

